import {type DateRangeGranularityType, INFINITY_SYMBOL} from './types';
import {type DateRangeInterval, type IntervalString, parseIntervalStringToObject} from './date-range-util';
import type {LocalizeFunction} from 'core/types';
import {exists} from 'shared/utils/custom-utils';
import {DateTime} from 'luxon';
import {isIntervalInvalid} from './date-range-util';
import {NEGATIVE_INFINITY, POSITIVE_INFINITY} from './date-range-util';

export function formatIntervalStringToReadableForm(intervalString: IntervalString | undefined | null, localizeFn: LocalizeFunction, granularity: DateRangeGranularityType = 'date-only'): string {
    if (!exists(intervalString) || intervalString.trim() === '') {
        return '';
    }

    return formatIntervalObjectToReadableForm(parseIntervalStringToObject(intervalString), localizeFn, granularity);
}

export function formatIntervalObjectToReadableForm(interval: DateRangeInterval, localizeFn: LocalizeFunction, granularity: DateRangeGranularityType = 'date-only'): string {
    const emptyLocalized = localizeFn(/* @kp-localization commons.EmptyDateRangeInterval */'commons.EmptyDateRangeInterval');
    const invalidLocalized = localizeFn(/* @kp-localization commons.InvalidDateRangeInterval */ 'commons.InvalidDateRangeInterval');
    const fromLocalized = localizeFn(/* @kp-localization commons.from */'commons.from');
    const toLocalized = localizeFn(/* @kp-localization commons.to */'commons.to');

    // Handle null/undefined intervals
    if (!exists(interval)) {
        return emptyLocalized;
    }

    // Handle invalid intervals
    if (isIntervalInvalid(interval)) {
        return invalidLocalized;
    }

    // Handle empty intervals
    if (!exists(interval.from) && !exists(interval.to)) {
        return emptyLocalized;
    }

    // Format individual bounds
    const formatBound = (bound: DateTime | typeof NEGATIVE_INFINITY | typeof POSITIVE_INFINITY | undefined): string => {
        if (!exists(bound)) {
            return '';
        }

        if (bound === NEGATIVE_INFINITY) {
            return `-${INFINITY_SYMBOL}`;
        }

        if (bound === POSITIVE_INFINITY) {
            return INFINITY_SYMBOL;
        }

        if (DateTime.isDateTime(bound)) {
            if (granularity === 'date-and-time') {
                return bound.toFormat('dd.MM.yyyy HH:mm');
            } else {
                return bound.toFormat('dd.MM.yyyy');
            }
        }

        return '';
    };

    const fromFormatted = formatBound(interval.from);
    const toFormatted = formatBound(interval.to);

    // Handle partial intervals
    if (!exists(interval.from) && exists(interval.to)) {
        return `${toLocalized} ${toFormatted}`;
    }

    if (exists(interval.from) && !exists(interval.to)) {
        return `${fromLocalized} ${fromFormatted}`;
    }

    // Handle complete intervals
    if (exists(interval.from) && exists(interval.to)) {
        return `${fromLocalized} ${fromFormatted} ${toLocalized} ${toFormatted}`;
    }

    return emptyLocalized;
}